import React, { ChangeEvent, useState } from "react";

import { zodResolver } from "@hookform/resolvers/zod";
import InputLabelWrapper from "components/basic/input-label-wrapper/InputLabelWrapper";
import {
  MdmDeviceDto,
  MdmObject,
  useDeleteDeviceMutation,
  useGetObjectsByProjectQuery,
  useGetProjectQuery,
} from "gql/generated/types";
import { Button } from "primereact/button";
import { Chips, ChipsChangeParams } from "primereact/chips";
import { ConfirmPopup, confirmPopup } from "primereact/confirmpopup";
import { Dropdown, DropdownChangeParams } from "primereact/dropdown";
import { InputText } from "primereact/inputtext";
import { TabPanel, TabView } from "primereact/tabview";
import { Controller, ControllerRenderProps, useForm } from "react-hook-form";
import { Link, useNavigate, useParams } from "react-router-dom";
import { T } from "services/lang/LanguageService";
import { DEFAULT_DATE_TIME_FORMATTER } from "utils/constants";
import { parseToLocalZoneDateTime } from "utils/time-utils";
import { z } from "zod";
import { apolloErrorToaster, getFormErrorMessage, successToaster } from "../utils/utils";
import AdditionalInfo from "./AdditionalInfo";
import AdditionalInfoEdit from "./AdditionalInfoEdit";
import DatasetsTab from "./DatasetsTab";
import DeviceStatusTag from "./DeviceStatus";
import MigrationHistory from "./MigrationHistory";
import StatusHistory from "./StatusHistory";

type DeviceFormProps = {
  device: Partial<MdmDeviceDto> | null;
  onSubmit: (device: Partial<MdmDeviceDto>) => void;
  newForm?: boolean;
  isDialog?: boolean;
  closeDialog?: () => void;
  loading?: boolean;
};

type DeviceFormType = { identifier: string; mdmObject: Partial<MdmObject> };

const defaultDevice: Partial<MdmDeviceDto> = {
  tags: [],
};

const schema = z.object({
  identifier: z.string().min(1, T("word.required-fields")),
  mdmObject: z.object({
    identifier: z.string(),
    id: z.string(),
  }),
});

const DeviceForm: React.FC<DeviceFormProps> = ({
  device,
  onSubmit,
  newForm = false,
  isDialog = false,
  closeDialog,
  loading = false,
}) => {
  const [deviceState, setDeviceState] = useState<Partial<MdmDeviceDto>>(device ?? defaultDevice);
  const [editMode, setEditMode] = useState(false);
  const navigate = useNavigate();

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<DeviceFormType>({
    resolver: zodResolver(schema),
  });

  const { projectId } = useParams();

  const { data: projectData } = useGetProjectQuery({ variables: { id: projectId! }, skip: !projectId });
  const deviceAttributes = projectData?.mdmProjectById?.configuration?.deviceConfiguration.customFields ?? [];
  const deviceTypes = projectData?.mdmProjectById?.configuration?.deviceConfiguration.measurementTypes ?? [];

  const { data: objectData } = useGetObjectsByProjectQuery({
    variables: { id: projectId! },
    skip: !projectId,
  });
  const [deleteDeviceMutation, { loading: loadingDelete }] = useDeleteDeviceMutation({
    onCompleted: () => {
      successToaster(T("alert.device-deleted-successfully"));
      navigate(-1);
    },
    onError: apolloErrorToaster,
    refetchQueries: ["GetObject"],
  });

  const handleChange = (e: ChangeEvent<HTMLInputElement> | ChipsChangeParams) => {
    const { name, value } = e.target;
    setDeviceState(prev => ({ ...prev, [name]: value }));
  };

  const handleDelete = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    confirmPopup({
      target: e.currentTarget,
      message: T("confirm-dialog.are-you-sure-you-want-to-delete"),
      icon: "pi pi-exclamation-triangle",
      acceptClassName: "p-button-danger",
      accept: () => {
        deleteDeviceMutation({
          variables: {
            id: deviceState.id!,
          },
          update(cache) {
            const normalizedId = cache.identify({ id: deviceState.id, __typename: "MdmDeviceDTO" });
            cache.evict({ id: normalizedId });
            cache.gc();
          },
        });
      },
    });
  };

  const handleDeviceTypeChange = (e: DropdownChangeParams) => {
    setDeviceState(prev => ({ ...prev, objectRelation: { ...prev.objectRelation, roleInObject: e.value, roleIdentifier: ""} }));
  }

  const handleRoleIdentifierChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setDeviceState(prev => ({
      ...prev,
      objectRelation: {
        ...prev.objectRelation,
        roleIdentifier: e.target.value,
      },
    }));
  }

  const handleDeviceConfigChange = (e: DropdownChangeParams) => {
    const config = projectData?.mdmProjectById?.deviceConfigs?.find(
      cfg => cfg?.id === e.value
    );
    setDeviceState(prev => ({ ...prev, config }));
  }

  const renderIdentifierControl = (field: ControllerRenderProps<DeviceFormType, any>) => (
    <InputLabelWrapper labelName={T("word.identifier")} col={3} >
      <InputText
        value={field.value}
        onChange={e => {
          field.onChange(e.target.value);
          handleChange(e);
        }}
        className="p-inputtext-sm"
        name="identifier"
        placeholder={T("word.identifier")}
      />
      {getFormErrorMessage("identifier", errors)}
    </InputLabelWrapper>
  );

  const renderMdmObjectControl = (field: ControllerRenderProps<DeviceFormType, any>) => (
    <InputLabelWrapper labelName={T("word.object")} col={3} >
      <Dropdown
        value={field.value}
        options={objectData?.mdmObjectsByProjectId.map(object => ({
          identifier: object.identifier,
          id: object.id,
        }))}
        onChange={e => {
          field.onChange(e.value);
          setDeviceState(prev => ({ ...prev, objectRelation: { ...prev.objectRelation, mdmObject: e.value } }));
        }}
        optionLabel="identifier"
        name="mdmObject"
        placeholder={T("word.object")}
        className="p-inputtext-sm"
        filter
      />
      {errors.mdmObject?.identifier && (
        <small className="p-error">{errors.mdmObject.identifier.message}</small>
      )}
    </InputLabelWrapper>
  );

  return (
    <div id="device-form" className="mx-auto">
      <ConfirmPopup />
      {!editMode && device && !newForm && (
        <div>
          <div className="flex justify-content-end align-items-center gap-2">
            {device && (
              <DeviceStatusTag
                status={device.status?.status!}
                lastMeasurementDate={device.status?.lastMeasurementDate}
              />
            )}
            <Button
              onClick={() => setEditMode(true)}
              type="button"
              title={T("action.edit")}
              className="p-button-rounded p-button-text p-button-sm"
              icon="pi pi-pencil"
            />
          </div>
          <div className="info-table-wrapper">
            <table className="info-table">
              <caption>{T("mdm.meter-details")}</caption>
              <tbody>
                <tr>
                  <th>{T("word.identifier")}:</th>
                  <td>{device?.identifier ?? "-"}</td>
                </tr>
                <tr>
                  <th>{T("word.object-identifier")}:</th>
                  <td>
                    {device?.objectRelation?.mdmObject?.identifier ? (
                      <Link
                        to={`/mdm/${projectId}/objects/${device?.objectRelation?.mdmObject?.id}`}
                      >
                        {device?.objectRelation?.mdmObject?.identifier}
                      </Link>
                    ) : (
                      "-"
                    )}
                  </td>
                </tr>
                <tr>
                  <th>{T("word.address")}:</th>
                  <td>{device?.objectRelation?.mdmObject?.address?.fullAddress ?? "-"}</td>
                </tr>
                <tr>
                  <th>{T("mdm.role")}:</th>
                  <td>{device?.objectRelation?.roleInObject ?? "-"}</td>
                </tr>
                <tr>
                  <th>{T("word.role-identifier")}:</th>
                  <td>{device?.objectRelation?.roleIdentifier ?? "-"}</td>
                </tr>
                <tr>
                  <th>{T("word.configuration")}:</th>
                  <td>{device?.config?.title ?? "-"}</td>
                </tr>
                <tr>
                  <th>{T("word.tags")}:</th>
                  <td>{device?.tags?.length ? device?.tags?.join(", ") : "-"}</td>
                </tr>
                <tr>
                  <th>{T("word.created")}:</th>
                  <td>
                    {parseToLocalZoneDateTime(device.createdAt).format(
                      DEFAULT_DATE_TIME_FORMATTER
                    ) ?? "-"}
                  </td>
                </tr>
                <tr>
                  <th>{T("word.updated")}:</th>
                  <td>
                    {parseToLocalZoneDateTime(device.modifiedAt).format(
                      DEFAULT_DATE_TIME_FORMATTER
                    ) ?? "-"}
                  </td>
                </tr>
              </tbody>
            </table>
            {device && deviceAttributes.length > 0 && (
              <AdditionalInfo attributes={deviceAttributes} objectState={deviceState} />
            )}
          </div>
        </div>
      )}
      {(newForm || editMode || !device) && (
        <form className="flex justify-content-between flex-column gap-5">
          <div className="grid p-fluid">
            <div className="col-3" />
            <Controller
              defaultValue={deviceState.identifier ?? ""}
              control={control}
              name="identifier"
              render={({ field }) => renderIdentifierControl(field)}
            />
            <Controller
              defaultValue={{
                identifier: deviceState.objectRelation?.mdmObject?.identifier!,
                id: deviceState.objectRelation?.mdmObject?.id,
              }}
              control={control}
              name="mdmObject"
              render={({ field }) => renderMdmObjectControl(field)}
            />
              <div className="col-3" />
              <div className="col-3" />
            <InputLabelWrapper labelName={T("word.configuration")} col={2}>
              <Dropdown
                value={deviceState.config?.id}
                options={projectData?.mdmProjectById?.deviceConfigs ?? []}
                optionLabel="title"
                placeholder={T("word.select-one")}
                optionValue="id"
                className="p-inputtext-sm"
                onChange={handleDeviceConfigChange}
              />
            </InputLabelWrapper>
            <InputLabelWrapper labelName={T("mdm.role")} col={2}>
              <Dropdown
                value={deviceState.objectRelation?.roleInObject}
                options={deviceTypes ?? []}
                optionLabel="title"
                optionValue="id"
                placeholder={T("word.select-one")}
                className="p-inputtext-sm"
                onChange={handleDeviceTypeChange}
                showClear
              />
            </InputLabelWrapper>
            <InputLabelWrapper labelName={T("word.role-identifier")} col={2}>
              <InputText
                value={deviceState.objectRelation?.roleIdentifier ?? ""}
                placeholder={T("word.role-identifier")}
                className="p-inputtext-sm"
                onChange={handleRoleIdentifierChange}
              />
            </InputLabelWrapper>
            <div className="col-3" />
            <div className="col-3" />
            <InputLabelWrapper labelName={T("word.tags")}>
              <Chips
                value={deviceState.tags ?? []}
                onChange={handleChange}
                className="p-inputtext-sm"
                name="tags"
                placeholder={`${T("word.tags")}..`}
              />
            </InputLabelWrapper>
            {deviceAttributes.length > 0 && (
              <AdditionalInfoEdit
                attributes={deviceAttributes}
                objectState={deviceState}
                setObjectState={setDeviceState}
                editMode={editMode}
              />
            )}
            <div className="flex w-full justify-content-end gap-3 mt-3">
              <Button
                type="submit"
                onClick={handleSubmit(() => {
                  onSubmit(deviceState);
                  setEditMode(false);
                })}
                label={newForm ? T("action.create") : T("action.update")}
                className={`p-button-rounded p-button-${newForm ? "success" : "primary"} w-10rem`}
                loading={loading}
              />
              {isDialog && (
                <Button
                  type="button"
                  label={T("action.close")}
                  onClick={closeDialog}
                  className="p-button-rounded p-button-secondary p-button-outlined w-10rem"
                />
              )}
              {device && !newForm && (
                <>
                  <Button
                    type="button"
                    label={T("action.delete")}
                    className="p-button-rounded p-button-danger w-10rem"
                    onClick={handleDelete}
                    loading={loadingDelete}
                  />
                  <Button
                    type="button"
                    onClick={() => {
                      setDeviceState(device ?? {});
                      setEditMode(false);
                    }}
                    label={T("word.cancel")}
                    className="p-button-rounded p-button-secondary p-button-outlined w-10rem"
                  />
                </>
              )}
              {newForm && !device && !isDialog && (
                <Button
                  type="button"
                  onClick={() => navigate(-1)}
                  label={T("action.back")}
                  className="p-button-rounded p-button-secondary p-button-outlined w-10rem"
                />
              )}
            </div>
          </div>
        </form>
      )}
      {!editMode && device && !newForm && (
        <TabView>
          <TabPanel header={T("word.datasets")}>
            <DatasetsTab datasets={device.boundDatasets?.all} />
          </TabPanel>
          <TabPanel header={T("mdm.status-history")}>
            <StatusHistory device={device} />
          </TabPanel>
          <TabPanel header={T("mdm.migration-history")}>
            <MigrationHistory device={device} />
          </TabPanel>
        </TabView>
      )}
    </div>
  );
};

export default DeviceForm;
